// The MCP prompts a bot's agents can reach.
//
// Served from the chat group rather than the admin one: the caller is a chat
// user, and the backend applies the same bot policy it applies to the bot list.
// Somebody who may not use a bot does not see what it offers either.

import { getAuthToken } from './api';

const BASE = '/api/v1/chat/bots';

// listBotPrompts returns one entry per MCP server, each with the prompts it
// offers and the agents in this bot that reach it.
//
// The prompt objects are the server's own prompts/list, passed through
// untouched, so anything the MCP spec adds arrives here without a change on
// either side.
export async function listBotPrompts(project, bot) {
  if (!project || !bot) return [];

  const token = await getAuthToken();
  const headers = {};

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const path = `${BASE}/${encodeURIComponent(project)}/${encodeURIComponent(bot)}/prompts`;
  const response = await fetch(path, { headers });

  if (!response.ok) {
    // Deliberately quiet for the caller: a bot with no prompts and a bot whose
    // prompt list could not be fetched both mean "no slash commands", and
    // neither is worth an error in front of somebody trying to type.
    const body = await response.json().catch(() => null);

    throw new Error(
      body?.error?.Message || body?.error?.message || `request failed (${response.status})`,
    );
  }

  const body = await response.json();

  return body?.data || [];
}

// renderBotPrompt runs one prompt through its MCP server and returns the text
// for the composer.
//
// Bot-scoped like the list is, and for the same reason: the server checks that
// this (imcp, prompt) pair is one this bot actually reaches, so naming another
// server in the path gets the same answer as naming a prompt that does not
// exist.
export async function renderBotPrompt(project, bot, imcp, prompt, args) {
  const token = await getAuthToken();
  const headers = { 'Content-Type': 'application/json' };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const path =
    `${BASE}/${encodeURIComponent(project)}/${encodeURIComponent(bot)}` +
    `/prompts/${encodeURIComponent(imcp)}/${encodeURIComponent(prompt)}`;

  const response = await fetch(path, {
    method: 'POST',
    headers,
    body: JSON.stringify({ arguments: args || {} }),
  });

  const body = await response.json().catch(() => null);

  if (!response.ok) {
    throw new Error(
      body?.error?.Message || body?.error?.message || `could not render the prompt (${response.status})`,
    );
  }

  return body?.data || null;
}

// completePromptArgument asks what one argument will take, given what is
// already filled.
//
// Live by necessity: an argument's candidates depend on the other arguments,
// so there is nothing to fetch at sync time. Called once per field rather than
// per keystroke, because every MCP call opens a fresh session and does the
// initialize handshake, and the browser can filter a complete list itself.
export async function completePromptArgument(project, bot, imcp, prompt, argument, value, filled) {
  const token = await getAuthToken();
  const headers = { 'Content-Type': 'application/json' };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const path =
    `${BASE}/${encodeURIComponent(project)}/${encodeURIComponent(bot)}` +
    `/prompts/${encodeURIComponent(imcp)}/${encodeURIComponent(prompt)}/complete`;

  const response = await fetch(path, {
    method: 'POST',
    headers,
    body: JSON.stringify({ argument, value: value || '', arguments: filled || {} }),
  });

  if (!response.ok) {
    // Suggestions are optional, so this still degrades to a plain text field
    // rather than blocking the form. Logged, though: swallowing it silently
    // makes "this server offers no completions" and "the request failed" the
    // same empty dropdown, and only one of those is worth fixing.
    console.warn(
      `[prompts] no completions for ${imcp}/${prompt}.${argument} (${response.status})`,
    );

    return { values: [], hasMore: false };
  }

  const body = await response.json().catch(() => null);

  return body?.data || { values: [], hasMore: false };
}
